import { Button, Flex, Pagination, Table, Tag, Typography, Switch } from "antd"
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { PushRole, useAuth } from "../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { PaginationOptions } from "../interfaces/PaginationOptions.interface";
import { useState } from "react";
import { Registration } from "../interfaces/DevicesResult.interface";
import dayjs from "dayjs";
import { Tag as TagData, TagValueType } from "../interfaces/Tag.interface";
import { tagRender } from "../components/InputHelpers/Tag.helper";
import { DeviceDetail } from "../components/DeviceDetail";
import { versionCodeToString } from "../utils/versionCode.helper.utils";


export const Devices = (props: { applicationId: string }) => {
    const [paginationOptions, setPaginationOptions] = useState<PaginationOptions>({ limit: 100, offset: 0 });
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [deviceDetail, setDeviceDetail] = useState<Registration|null>(null)
    const [showDeviceDetail, setShowDeviceDetail] = useState<boolean>(false);
    const auth = useAuth();

    const deviceQuery = useQuery({ staleTime: 300000, queryKey: ['devices', props.applicationId, 'offset', paginationOptions.offset, 'limit', paginationOptions.limit], queryFn: () => auth.api.getDevices(props.applicationId, paginationOptions) })


    const onPaginationChange = (page: number, pageSize: number) => {
        const newOffset = (page - 1) * pageSize;
        setPaginationOptions({ offset: newOffset, limit: pageSize });
        setCurrentPage(page);
    }
    const allowedTagsToColumnDefinitions = (tags: TagData[], handleSetTag: (deviceId: string | undefined,tagName:string, value:boolean) => void, devices: Registration[]) => {
        
        return tags
            .filter(tag => tag.meta?.displayOnListing ?? true)
            .map(tag => {
                return {
                    title: tag.label,
                    dataIndex: '.',
                    key: tag.name,
                    render: (value: any, record: Registration) => {
                        const actualValue = (record.tags as any)[tag.name];
    
                        if (tag.type === TagValueType.Boolean) {
                            return (
                                <Switch
                                    checked={!!actualValue}
                                    disabled={!tag.meta?.editable}
                                    onChange={(checked) => handleSetTag(record.id, tag.name, checked)}
                                    checkedChildren={<CheckOutlined />}
                                    unCheckedChildren={<CloseOutlined />}
                                />
                            );
                        } else if (tag.type === TagValueType.Enum_String || tag.type === TagValueType.String) {
                            let displayValue = actualValue;
                            if (displayValue && tag.meta.allowedValues) {
                                const found = tag.meta.allowedValues.find(x => x.value == displayValue);
                                if (found) displayValue = found.label;
                            }
                            return displayValue;
                        } else if (tag.type === TagValueType.Number && tag.meta.isVersionCode && tag.meta.versionCodePadding) {
                            if (actualValue)
                                return versionCodeToString(actualValue, tag.meta.versionCodePadding);
                            return actualValue;
                        } else {
                            return `${actualValue}`;
                        }
                    }
                };
            })
            .sort((a, b) => a.title.localeCompare(b.title));
    };
    
    const showDeviceDetails = (device:Registration)=> {
        console.log("showDeviceDetails", device);
        setDeviceDetail(device);
        setShowDeviceDetail(true);
    }

    const setTagOfDevice = async (deviceId: string | undefined,tagName:string, value: boolean) => {
        if (!deviceId) {
            console.error("Device ID is undefined");
            return;
        }
        try {
            await auth.api.setDeviceTag(props.applicationId,deviceId,tagName,value);
            deviceQuery.refetch();
        } catch (error) {
            console.error("Failed to mark as test device", error);
        }
    };

    const getDeviceColumns = () => {
        let preCols = []
        if (auth.hasClaims(props.applicationId, PushRole.CanUseDeveloperTools)) {
            preCols.push({ title: 'Id', dataIndex: 'id', key: 'id' })
        }
        preCols.push({
            title: 'Last Seen', dataIndex: 'lastSeen', key: 'lastSeen', render: (value: string | Date) => {
                let date = new Date(value)
                return date.toLocaleDateString() + ` ` + date.toLocaleTimeString();
            }
        });
        if (auth.hasClaims(props.applicationId, PushRole.CanUseDeveloperTools)) {
            preCols.push({ title: 'fcmToken', dataIndex: 'fcmToken', key: 'fcmToken' })
        }

        const columns = allowedTagsToColumnDefinitions(
            deviceQuery.data?.tags ?? [],
            setTagOfDevice,
            deviceQuery.data?.registrations ?? []
        );

        return [
            ...preCols,
            ...columns,
            { title: "View", dataIndex: '.', key:'view', render: (value: any, device:Registration)=>(<Flex><Button onClick={()=>showDeviceDetails(device)}>View</Button></Flex>)}
        ]
    }
    return (
        <Flex vertical gap={10} style={{ maxHeight: '100%' }} wrap={"nowrap"}>
           <Flex justify="space-between"><Typography.Title level={3}>Devices</Typography.Title></Flex>
            <Table
                columns={getDeviceColumns()}
                size={"middle"}
                loading={deviceQuery.isFetching}
                dataSource={deviceQuery.data?.registrations ?? []}
                rowKey={(device: Registration) => device.id ?? ""}
                pagination={false}
                sticky
            >
            </Table>
            <Pagination 
                current={currentPage}
                onChange={onPaginationChange} 
                total={deviceQuery.data?.total ?? 0} 
                pageSize={paginationOptions.limit} 
                pageSizeOptions={[10, 20, 50, 100]} 
                showSizeChanger 
            />
            {showDeviceDetail && deviceDetail && <DeviceDetail applicationId={props.applicationId} device={deviceDetail} isOpen={showDeviceDetail} tags={deviceQuery.data?.tags ?? []} onDismiss={()=>setShowDeviceDetail(false)}/>}
        </Flex>
    )

}